// ----------------------------------------------------------------------

import { _id } from 'src/_mock/assets';

const ROOTS = {
  AUTH: '/auth',
  DASHBOARD: '/dashboard',
  MANAGEMENT: '/management',
  SETTINGS: '/agency',
  REPORTS: '/reports',
  PROCEDURE: '/procedure',
  SHIPPING: '/shipping',
  DISTRIBUTION: '/distribution',
  LOCKER: '/locker',
  PORTAL: '/portal',
  COURIER: '/courier',
  ADMIN: '/admin',
  BILLING: '/billing',
  OWNER: '/owner',
};

const MOCK_ID = _id[1];

// ----------------------------------------------------------------------

export const paths = {
  minimalUI: 'https://mui.com/store/items/minimal-dashboard/',
  // AUTH
  auth: {
    jwt: {
      login: `${ROOTS.AUTH}/jwt/login`,
      register: `${ROOTS.AUTH}/jwt/register`,
    },
    auth0: {
      login: `${ROOTS.AUTH}/auth0/login`,
    },
  },
  legal: {
    termsAndConditions: '/terms-and-conditions',
    privacyPolicy: '/privacy-policy',
  },
  product: {
    root: `/product`,
    checkout: `/product/checkout`,
    details: (id: string) => `/product/${id}`,
  },
  // PLATFORM ADMIN (epic #45) — super-admin portal above all agencies
  admin: {
    root: ROOTS.ADMIN,
    agencies: `${ROOTS.ADMIN}/agencies`,
    // Slice 5 (#50): read-only view-as support, audit trail, platform-admin management.
    agencyOverview: (number: string) => `${ROOTS.ADMIN}/agencies/${number}/overview`,
    audit: `${ROOTS.ADMIN}/audit`,
    admins: `${ROOTS.ADMIN}/admins`,
    // Slice 1 (#57): platform billing config (rate card + per-agency profiles).
    billing: `${ROOTS.ADMIN}/billing`,
  },
  // COURIER — public, token-gated proof-of-delivery capture page (PRD #108, issue #106).
  // No Auth0: the URL token is the only credential. Lives OUTSIDE AuthGuard.
  courier: {
    root: ROOTS.COURIER,
    capture: (token: string) => `${ROOTS.COURIER}/capture/${token}`,
  },
  // DASHBOARD
  dashboard: {
    root: ROOTS.DASHBOARD,
    pagedashboard: `${ROOTS.DASHBOARD}/pagedashboard`,
    pageOne: `${ROOTS.DASHBOARD}/pageone`,
  },
  // BILLING (owner-only, #59) — agency owner's read-only mid-month accrual.
  billing: {
    root: ROOTS.BILLING,
    accrual: `${ROOTS.BILLING}/accrual`,
  },
  // OWNER (owner-only) — owner-facing dashboards. Daily Cash Close (spec #122, slice #123).
  owner: {
    root: ROOTS.OWNER,
    cashClose: `${ROOTS.OWNER}/cash-close`,
  },
  settings: {
    root: ROOTS.SETTINGS,
    servicesType: `${ROOTS.SETTINGS}/services-type`,
    packagesType: `${ROOTS.SETTINGS}/packages-type`,
    deliveryType: `${ROOTS.SETTINGS}/delivery-type`,
    remittanceDeliveryType: `${ROOTS.SETTINGS}/remittance-delivery-type`,
    migrationForms: `${ROOTS.SETTINGS}/migration-forms`,
    contentType: `${ROOTS.SETTINGS}/content-type`,
    contentTypeRange: `${ROOTS.SETTINGS}/content-type-range`,
    contentCategory: `${ROOTS.SETTINGS}/content-category`,
    products: {
      root: `${ROOTS.SETTINGS}/products`,
      list: `${ROOTS.SETTINGS}/products`,
      new: `${ROOTS.SETTINGS}/products/new`,
      edit: (id: string) => `${ROOTS.SETTINGS}/products/${id}/edit`,
    },
    pickupLocation: `${ROOTS.SETTINGS}/pickup-location`,
    remittancePickupLocation: `${ROOTS.SETTINGS}/remittance-pickup-location`,
    provincia: `${ROOTS.SETTINGS}/provincia`,
    municipio: `${ROOTS.SETTINGS}/municipio`,
    payment: `${ROOTS.SETTINGS}/payment`,
    passportCategoryType: `${ROOTS.SETTINGS}/passport-category-type`,
  },
  reports: {
    root: ROOTS.REPORTS,
    clientTransactions: `${ROOTS.REPORTS}/client-transactions`,
    expiringPassports: `${ROOTS.REPORTS}/expiring-passports`,
  },
  management: {
    client: {
      root: `${ROOTS.MANAGEMENT}/client`,
      new: `${ROOTS.MANAGEMENT}/client/new`,
      list: `${ROOTS.MANAGEMENT}/client/list`,
      account: `${ROOTS.MANAGEMENT}/client/account`,
      driverLicenseProcessing: `${ROOTS.MANAGEMENT}/client/driver-license-processing`,
      edit: (id: string) => `${ROOTS.MANAGEMENT}/client/${id}/edit`,
      demo: {
        edit: `${ROOTS.MANAGEMENT}/client/${MOCK_ID}/edit`,
      },
    },
    employee: {
      root: `${ROOTS.MANAGEMENT}/employee`,
      new: `${ROOTS.MANAGEMENT}/employee/new`,
      list: `${ROOTS.MANAGEMENT}/employee/list`,
      edit: (id: string) => `${ROOTS.MANAGEMENT}/employee/${id}/edit`,
    },
  },
  procedure: {
    passport: {
      root: `${ROOTS.PROCEDURE}/passport`,
      new: `${ROOTS.PROCEDURE}/passport/new`,
      photoProcessing: `${ROOTS.PROCEDURE}/passport/photo-processing`,
      newPassportClient: (clientId: string) => `${ROOTS.PROCEDURE}/passport/${clientId}/client`,
      list: `${ROOTS.PROCEDURE}/passport/list`,
      edit: (id: string) => `${ROOTS.PROCEDURE}/passport/${id}/edit`,
      details: (id: string) => `${ROOTS.PROCEDURE}/passport/${id}`,
    },
    migration: {
      root: `${ROOTS.PROCEDURE}/migration`,
      new: `${ROOTS.PROCEDURE}/migration/new`,
      newMigrationClient: (clientId: string) => `${ROOTS.PROCEDURE}/migration/${clientId}/client`,
      list: `${ROOTS.PROCEDURE}/migration/list`,
      edit: (id: string) => `${ROOTS.PROCEDURE}/migration/${id}/edit`,
      details: (id: string) => `${ROOTS.PROCEDURE}/migration/${id}`,
    },
    remittance: {
      root: `${ROOTS.PROCEDURE}/remittance`,
      new: `${ROOTS.PROCEDURE}/remittance/new`,
      newRemittanceClient: (clientId: string) => `${ROOTS.PROCEDURE}/remittance/${clientId}/client`,
      newRemittanceRecipient: (clientId: string, recipientId: string) =>
        `${ROOTS.PROCEDURE}/remittance/${clientId}/client/${recipientId}/recipient`,
      list: `${ROOTS.PROCEDURE}/remittance/list`,
      edit: (id: string) => `${ROOTS.PROCEDURE}/remittance/${id}/edit`,
      details: (id: string) => `${ROOTS.PROCEDURE}/remittance/${id}`,
    },
  },
  shipping: {
    courier: {
      root: `${ROOTS.SHIPPING}/courier`,
      new: `${ROOTS.SHIPPING}/courier/new`,
      list: `${ROOTS.SHIPPING}/courier/list`,
      edit: (id: string) => `${ROOTS.SHIPPING}/courier/${id}/edit`,
    },
    dispatcher: {
      root: `${ROOTS.SHIPPING}/dispatcher`,
      new: `${ROOTS.SHIPPING}/dispatcher/new`,
      list: `${ROOTS.SHIPPING}/dispatcher/list`,
      edit: (id: string) => `${ROOTS.SHIPPING}/dispatcher/${id}/edit`,
    },
    provider: {
      root: `${ROOTS.SHIPPING}/provider`,
      new: `${ROOTS.SHIPPING}/provider/new`,
      list: `${ROOTS.SHIPPING}/provider/list`,
      edit: (id: string) => `${ROOTS.SHIPPING}/provider/${id}/edit`,
    },
    shipping: {
      root: `${ROOTS.SHIPPING}/shipping`,
      new: `${ROOTS.SHIPPING}/shipping/new`,
      newShippingClient: (clientId: string) => `${ROOTS.SHIPPING}/shipping/${clientId}/client`,

      newShippingRecipient: (clientId: string, recipientId: string) =>
        `${ROOTS.SHIPPING}/shipping/${clientId}/client/${recipientId}/recipient`,
      list: `${ROOTS.SHIPPING}/shipping/list`,
      details: (id: string) => `${ROOTS.SHIPPING}/shipping/${id}`,
      edit: (id: string) => `${ROOTS.SHIPPING}/shipping/${id}/edit`,
      demo: {
        details: `${ROOTS.SHIPPING}/shipping/${MOCK_ID}`,
        edit: `${ROOTS.SHIPPING}/shipping/${MOCK_ID}/edit`,
      },
    },
    dispatch: {
      root: `${ROOTS.SHIPPING}/dispatch`,
      new: `${ROOTS.SHIPPING}/dispatch/new`,
      list: `${ROOTS.SHIPPING}/dispatch/list`,
      details: (id: string) => `${ROOTS.SHIPPING}/dispatch/${id}`,
      edit: (id: string) => `${ROOTS.SHIPPING}/dispatch/${id}/edit`,
    },
    masterDispatch: {
      root: `${ROOTS.SHIPPING}/master-dispatch`,
      new: `${ROOTS.SHIPPING}/master-dispatch/new`,
      list: `${ROOTS.SHIPPING}/master-dispatch/list`,
      details: (id: string) => `${ROOTS.SHIPPING}/master-dispatch/${id}`,
      edit: (id: string) => `${ROOTS.SHIPPING}/master-dispatch/${id}/edit`,
    },
    receptor: {
      root: `${ROOTS.SHIPPING}/receptor`,
      new: `${ROOTS.SHIPPING}/receptor/new`,
      list: `${ROOTS.SHIPPING}/receptor/list`,
      edit: (id: string) => `${ROOTS.SHIPPING}/receptor/${id}/edit`,
    },
    manifest: {
      root: `${ROOTS.SHIPPING}/manifest`,
      new: `${ROOTS.SHIPPING}/manifest/new`,
      list: `${ROOTS.SHIPPING}/manifest/list`,
      details: (id: string) => `${ROOTS.SHIPPING}/manifest/${id}`,
    },
    reception: {
      root: `${ROOTS.SHIPPING}/reception`,
      new: `${ROOTS.SHIPPING}/reception/new`,
      list: `${ROOTS.SHIPPING}/reception/list`,
      details: (id: string) => `${ROOTS.SHIPPING}/reception/${id}`,
    },
    distribution: {
      root: `${ROOTS.SHIPPING}/distribution`,
      new: `${ROOTS.SHIPPING}/distribution/new`,
      list: `${ROOTS.SHIPPING}/distribution/list`,
      details: (id: string) => `${ROOTS.SHIPPING}/distribution/${id}`,
    },
    distributorManifest: {
      root: `${ROOTS.SHIPPING}/distributor-manifest`,
      list: `${ROOTS.SHIPPING}/distributor-manifest/list`,
      details: (id: string) => `${ROOTS.SHIPPING}/distributor-manifest/${id}`,
    },
    distributionClient: {
      root: `${ROOTS.DISTRIBUTION}/manifests`,
      upload: `${ROOTS.DISTRIBUTION}/manifests/upload`,
      list: `${ROOTS.DISTRIBUTION}/manifests`,
      details: (id: string) => `${ROOTS.DISTRIBUTION}/manifests/${id}`,
      profile: `${ROOTS.DISTRIBUTION}/profile`,
    },
    distributionClientManagement: {
      root: `${ROOTS.SHIPPING}/distribution-client`,
      new: `${ROOTS.SHIPPING}/distribution-client/new`,
      list: `${ROOTS.SHIPPING}/distribution-client/list`,
      edit: (id: string) => `${ROOTS.SHIPPING}/distribution-client/${id}/edit`,
    },
    claim: {
      root: `${ROOTS.SHIPPING}/claim`,
      new: `${ROOTS.SHIPPING}/claim/new`,
      newClaimShipping: (shippingId: string) => `${ROOTS.SHIPPING}/claim/new?shipmentId=${shippingId}`,
      newClaimDispatch: (dispatchId: string) => `${ROOTS.SHIPPING}/claim/new?dispatchId=${dispatchId}`,
      list: `${ROOTS.SHIPPING}/claim/list`,
      details: (id: string) => `${ROOTS.SHIPPING}/claim/${id}`,
      edit: (id: string) => `${ROOTS.SHIPPING}/claim/${id}/edit`,
    },
  },
  locker: {
    root: ROOTS.LOCKER,
    packages: `${ROOTS.LOCKER}/packages`,
    detail: (id: string) => `${ROOTS.LOCKER}/packages/${id}`,
  },
  portal: {
    root: ROOTS.PORTAL,
    home: `${ROOTS.PORTAL}/home`,
    packages: `${ROOTS.PORTAL}/packages`,
    shipments: `${ROOTS.PORTAL}/shipments`,
    shipment: (id: string) => `${ROOTS.PORTAL}/shipments/${id}`,
    passports: `${ROOTS.PORTAL}/passports`,
    passport: (id: string) => `${ROOTS.PORTAL}/passports/${id}`,
    remittances: `${ROOTS.PORTAL}/remittances`,
    remittance: (id: string) => `${ROOTS.PORTAL}/remittances/${id}`,
    migrations: `${ROOTS.PORTAL}/migrations`,
    migration: (id: string) => `${ROOTS.PORTAL}/migrations/${id}`,
  },
};
